import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { DateTimeFieldComponent, ErrorBlockComponent } from '@clinic-platform/ui-components';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { CreateSpecialistDowntimeRequest } from '../data-access/specialist-downtime.models';

export type SpecialistDowntimeClinicOption = {
  id: string;
  label: string;
  workingDayStart?: string | null;
  workingDayEnd?: string | null;
  slotDurationMinutes?: number | null;
};

@Component({
  selector: 'app-specialist-downtime-form',
  standalone: true,
  imports: [ReactiveFormsModule, DateTimeFieldComponent, ErrorBlockComponent, TranslatePipe],
  template: `
    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      class="downtime-form"
    >
      <div class="downtime-form__grid">
        <div class="downtime-form__field">
          <label for="specialist-downtime-clinicId">{{ 'Clinic' | t }}</label>
          @if (clinics.length > 0) {
            <select id="specialist-downtime-clinicId" formControlName="clinicId">
              <option value="">{{ 'Select clinic' | t }}</option>
              @for (item of clinics; track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
          } @else {
            <input
              id="specialist-downtime-clinicId"
              [value]="'Clinic catalog is unavailable' | t"
              disabled
            />
          }
        </div>

        <div class="downtime-form__field">
          <label for="specialist-downtime-specialistUserId">{{ 'Specialist' | t }}</label>
          @if (specialists.length > 0) {
            <select id="specialist-downtime-specialistUserId" formControlName="specialistUserId">
              <option value="">{{ 'Select specialist' | t }}</option>
              @for (item of filteredSpecialists; track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
          } @else {
            <input
              id="specialist-downtime-specialistUserId"
              [value]="'Specialists catalog is unavailable' | t"
              disabled
            />
          }
        </div>

        <div class="downtime-form__field">
          <label for="specialist-downtime-startTime">{{ 'Start Time' | t }}</label>
          <lib-date-time-field
            inputId="specialist-downtime-startTime"
            formControlName="startTime"
            [defaultToToday]="true"
            [minTime]="selectedClinicWorkingDayStart"
            [maxTime]="selectedClinicWorkingDayEnd"
            [timeStepMinutes]="selectedClinicSlotDurationMinutes"
            (datePartChange)="copyStartDateToEnd($event)"
            (timePartChange)="copyStartTimeToEnd($event)"
          />
        </div>

        <div class="downtime-form__field">
          <label for="specialist-downtime-expectedRepairUntil">{{ 'Expected Repair Until' | t }}</label>
          <lib-date-time-field
            inputId="specialist-downtime-expectedRepairUntil"
            formControlName="expectedRepairUntil"
            [defaultToToday]="true"
            [minTime]="selectedClinicWorkingDayStart"
            [maxTime]="selectedClinicWorkingDayEnd"
            [timeStepMinutes]="selectedClinicSlotDurationMinutes"
            [dateDraft]="endDateDraft"
            [timeDraft]="endTimeDraft"
            (datePartChange)="onEndDateChanged($event)"
            (timePartChange)="onEndTimeChanged($event)"
          />
        </div>

        <div class="downtime-form__field downtime-form__field--full">
          <label for="specialist-downtime-reason">{{ 'Reason' | t }}</label>
          <input id="specialist-downtime-reason" formControlName="reason" />
        </div>
      </div>

      @if (errorText) {
        <lib-error-block variant="error" [message]="(errorText | t) ?? ''" />
      }

      @if (validationErrorText) {
        <lib-error-block variant="error" [message]="(validationErrorText | t) ?? ''" />
      }

      @if (catalogUnavailableReason) {
        <lib-error-block variant="error" [message]="(catalogUnavailableReason | t) ?? ''" />
      }

      <button
        type="submit"
        [disabled]="isSubmitting || !!catalogUnavailableReason"
        class="downtime-form__submit"
      >
        {{ (isSubmitting ? 'Creating...' : 'Create downtime') | t }}
      </button>
    </form>
  `,
  styles: [`
    .downtime-form {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 20px;
    }

    .downtime-form__grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
      margin-bottom: 16px;
    }

    .downtime-form__field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .downtime-form__field--full {
      grid-column: 1 / -1;
    }

    .downtime-form__field label {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .downtime-form__field input,
    .downtime-form__field select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 12px;
      font-size: 14px;
      box-sizing: border-box;
    }

    .downtime-form__submit {
      margin-top: 16px;
      height: 40px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 16px;
      cursor: pointer;
    }

    .downtime-form__submit:disabled {
      opacity: 0.7;
      cursor: default;
    }

    @media (max-width: 900px) {
      .downtime-form {
        padding: 18px;
      }

      .downtime-form__grid {
        grid-template-columns: minmax(0, 1fr);
      }
    }

    @media (max-width: 640px) {
      .downtime-form {
        padding: 16px;
        border-radius: 16px;
      }

      .downtime-form__submit {
        width: 100%;
      }
    }
  `]
})
export class SpecialistDowntimeFormComponent implements OnChanges
{
  private readonly formBuilder = inject(FormBuilder);

  @Input()
  public isSubmitting = false;

  @Input()
  public errorText: string | null = null;

  @Input()
  public clinics: SpecialistDowntimeClinicOption[] = [];

  @Input()
  public specialists: Array<{ id: string; label: string; clinicId: string | null }> = [];

  @Input()
  public initialValue: CreateSpecialistDowntimeRequest | null = null;

  @Output()
  public readonly submitted = new EventEmitter<CreateSpecialistDowntimeRequest>();

  protected validationErrorText: string | null = null;

  protected readonly form = this.formBuilder.group({
    clinicId: ['', [Validators.required]],
    specialistUserId: ['', [Validators.required]],
    startTime: ['', [Validators.required]],
    expectedRepairUntil: ['', [Validators.required]],
    reason: ['', [Validators.required]]
  });

  protected endDateDraft: string | null = null;
  protected endTimeDraft: string | null = null;

  private autoCopiedEndDate: string | null = null;
  private autoCopiedEndTime: string | null = null;

  protected get filteredSpecialists(): Array<{ id: string; label: string; clinicId: string | null }>
  {
    const clinicId = this.form.getRawValue().clinicId?.trim() ?? '';
    if (!clinicId) {
      return this.specialists;
    }

    return this.specialists.filter((item) => !item.clinicId || item.clinicId === clinicId);
  }

  protected get selectedClinicWorkingDayStart(): string | null
  {
    return this.selectedClinic?.workingDayStart ?? null;
  }

  protected get selectedClinicWorkingDayEnd(): string | null
  {
    return this.selectedClinic?.workingDayEnd ?? null;
  }

  protected get selectedClinicSlotDurationMinutes(): number
  {
    const value = this.selectedClinic?.slotDurationMinutes;
    return typeof value === 'number' && value > 0 ? value : 15;
  }

  protected get catalogUnavailableReason(): string | null
  {
    if (this.clinics.length === 0 && this.specialists.length === 0) {
      return 'Clinic and specialists catalogs are unavailable. Reload page context and try again.';
    }

    if (this.clinics.length === 0) {
      return 'Clinic catalog is unavailable. Reload page context and try again.';
    }

    if (this.specialists.length === 0) {
      return 'Specialists catalog is unavailable. Reload page context and try again.';
    }

    return null;
  }

  public constructor()
  {
    this.form.controls.clinicId.valueChanges.subscribe(() =>
    {
      this.resetEndCopyState();
      this.form.patchValue(
        {
          startTime: '',
          expectedRepairUntil: '',
        },
        { emitEvent: false },
      );

      const selectedSpecialistId = this.form.getRawValue().specialistUserId?.trim() ?? '';
      if (!selectedSpecialistId) {
        return;
      }

      const stillAvailable = this.filteredSpecialists.some((item) => item.id === selectedSpecialistId);
      if (!stillAvailable) {
        this.form.patchValue({ specialistUserId: '' }, { emitEvent: false });
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['clinics'])
    {
      this.selectSingleClinic();
    }

    if (changes['specialists'])
    {
      this.selectSingleSpecialist();
    }

    if (changes['initialValue'])
    {
      this.applyInitialValue();
    }
  }

  protected onSubmit(): void
  {
    if (this.catalogUnavailableReason) {
      this.validationErrorText = this.catalogUnavailableReason;
      return;
    }

    if (this.form.invalid)
    {
      this.form.markAllAsTouched();
      this.validationErrorText = this.buildValidationErrorText();
      return;
    }

    if (!this.hasValidTimeWindow())
    {
      this.validationErrorText = 'Expected repair time must be later than start time.';
      return;
    }

    this.validationErrorText = null;
    const value = this.form.getRawValue();

    this.submitted.emit({
      clinicId: value.clinicId ?? '',
      specialistUserId: value.specialistUserId ?? '',
      startTime: this.toLocalDateTimeSeconds(value.startTime),
      expectedRepairUntil: this.toLocalDateTimeSeconds(value.expectedRepairUntil),
      reason: value.reason ?? ''
    });
  }

  protected copyStartDateToEnd(value: string): void
  {
    if (!value) {
      return;
    }

    const currentEndDate = this.datePart(this.form.getRawValue().expectedRepairUntil) ?? this.endDateDraft;
    if (currentEndDate && currentEndDate !== this.autoCopiedEndDate) {
      return;
    }

    this.autoCopiedEndDate = value;
    this.endDateDraft = value;
    this.patchEndFromParts(value, this.timePart(this.form.getRawValue().expectedRepairUntil) ?? this.endTimeDraft);
  }

  protected copyStartTimeToEnd(value: string): void
  {
    if (!value) {
      return;
    }

    const currentEndTime = this.timePart(this.form.getRawValue().expectedRepairUntil) ?? this.endTimeDraft;
    if (currentEndTime && currentEndTime !== this.autoCopiedEndTime) {
      return;
    }

    this.autoCopiedEndTime = value;
    this.endTimeDraft = value;
    this.patchEndFromParts(this.datePart(this.form.getRawValue().expectedRepairUntil) ?? this.endDateDraft, value);
  }

  protected onEndDateChanged(value: string): void
  {
    this.endDateDraft = value;
    if (value && value !== this.autoCopiedEndDate) {
      this.autoCopiedEndDate = null;
    }
  }

  protected onEndTimeChanged(value: string): void
  {
    this.endTimeDraft = value;
    if (value && value !== this.autoCopiedEndTime) {
      this.autoCopiedEndTime = null;
    }
  }

  private toLocalDateTimeSeconds(value: string | null | undefined): string
  {
    const source = value?.trim() ?? '';

    if (!source)
    {
      return '';
    }

    return source.length === 16
      ? `${source}:00`
      : source;
  }

  private selectSingleClinic(): void
  {
    if (this.clinics.length !== 1)
    {
      return;
    }

    const currentClinicId = this.form.getRawValue().clinicId?.trim() ?? '';
    if (currentClinicId)
    {
      return;
    }

    this.form.patchValue({ clinicId: this.clinics[0].id });
  }

  private selectSingleSpecialist(): void
  {
    if (this.specialists.length !== 1)
    {
      return;
    }

    const currentSpecialistId = this.form.getRawValue().specialistUserId?.trim() ?? '';
    if (currentSpecialistId)
    {
      return;
    }

    this.form.patchValue({ specialistUserId: this.specialists[0].id });
  }

  private buildValidationErrorText(): string
  {
    const value = this.form.getRawValue();

    if (!value.clinicId?.trim()) {
      return 'Select clinic before creating downtime.';
    }

    if (!value.specialistUserId?.trim()) {
      return 'Select specialist before creating downtime.';
    }

    if (!value.startTime?.trim()) {
      return 'Select downtime start time.';
    }

    if (!value.expectedRepairUntil?.trim()) {
      return 'Select expected repair time.';
    }

    if (!value.reason?.trim()) {
      return 'Enter downtime reason.';
    }

    return 'Fill all required downtime fields.';
  }

  private hasValidTimeWindow(): boolean
  {
    const value = this.form.getRawValue();
    const startTime = value.startTime?.trim() ?? '';
    const expectedRepairUntil = value.expectedRepairUntil?.trim() ?? '';

    if (!startTime || !expectedRepairUntil) {
      return false;
    }

    return new Date(startTime).getTime() < new Date(expectedRepairUntil).getTime();
  }

  private get selectedClinic(): SpecialistDowntimeClinicOption | null
  {
    const clinicId = this.form.getRawValue().clinicId?.trim() ?? '';
    return this.clinics.find((item) => item.id === clinicId) ?? null;
  }

  private patchEndFromParts(date: string | null | undefined, time: string | null | undefined): void
  {
    if (!date || !time) {
      return;
    }

    this.form.patchValue({ expectedRepairUntil: `${date}T${time}` }, { emitEvent: false });
  }

  private datePart(value: string | null | undefined): string | null
  {
    const match = /^(\d{4}-\d{2}-\d{2})T/.exec(value?.trim() ?? '');
    return match?.[1] ?? null;
  }

  private timePart(value: string | null | undefined): string | null
  {
    const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value?.trim() ?? '');
    return match?.[2] ?? null;
  }

  private resetEndCopyState(): void
  {
    this.endDateDraft = null;
    this.endTimeDraft = null;
    this.autoCopiedEndDate = null;
    this.autoCopiedEndTime = null;
  }

  private applyInitialValue(): void
  {
    if (!this.initialValue) {
      return;
    }

    const startTime = this.toDateTimeInputValue(this.initialValue.startTime);
    const expectedRepairUntil = this.toDateTimeInputValue(this.initialValue.expectedRepairUntil);

    this.form.patchValue(
      {
        clinicId: this.initialValue.clinicId,
        specialistUserId: this.initialValue.specialistUserId,
        startTime,
        expectedRepairUntil,
        reason: this.initialValue.reason,
      },
      { emitEvent: false },
    );

    this.endDateDraft = this.datePart(expectedRepairUntil);
    this.endTimeDraft = this.timePart(expectedRepairUntil);
    this.autoCopiedEndDate = null;
    this.autoCopiedEndTime = null;
    this.validationErrorText = null;
  }

  private toDateTimeInputValue(value: string | null | undefined): string
  {
    const source = value?.trim() ?? '';
    return source.length >= 16 ? source.slice(0, 16) : source;
  }
}
